import { api } from './api.js';
import { toast } from './toast.js';
import { ct } from './i18n.js';

/**
 * What this server has configured that no longer works.
 *
 * The audit lives on the server — it reads the database and Discord — so the
 * page asks for it rather than computing it, and asks **after** painting: a
 * dashboard that waited for this would be slower for everybody in order to
 * serve the few who have something broken.
 *
 * Sentences arrive translated. Having the browser turn `roleTooHigh` into a
 * sentence would be a second place for that sentence to live, and the two would
 * disagree the day one of them is corrected.
 */

interface HealthFinding {
  kind: string;
  where: string;
  message: string;
}

interface HealthGroup {
  module: string;
  label: string;
  icon: string;
  findings: { kind: string; detail: string }[];
}

interface Health {
  checked: boolean;
  total?: number;
  groups?: HealthGroup[];
  findings: HealthFinding[];
}

const guildId = (): string => new URLSearchParams(window.location.search).get('guildId') ?? '';

async function read(): Promise<Health | null> {
  const id = guildId();
  if (!id) return null;

  try {
    return await api.get<Health>(`/api/health?guildId=${encodeURIComponent(id)}`);
  } catch {
    // A page must not lose its dashboard because an audit failed.
    return null;
  }
}

/**
 * How a fault reads at a glance.
 *
 * Two families, and the difference is what the reader has to do. A reference
 * to something deleted is a setting to change on this site; a role above the
 * bot or a channel it cannot write in is a permission to change on Discord,
 * with the setting itself perfectly good. Colouring them alike would hide the
 * one distinction that decides where to go.
 */
const GONE = new Set(['channelGone', 'roleGone', 'messageGone', 'messageChannelGone']);

/** How many lines a module shows before the rest is folded away. */
const SHOWN = 3;

function dot(kind: string): HTMLElement {
  const mark = document.createElement('span');

  mark.className = `mt-1.5 size-1.5 shrink-0 rounded-full ${GONE.has(kind) ? 'bg-error' : 'bg-warning'}`;
  return mark;
}

function line(finding: { kind: string; detail: string }): HTMLElement {
  const item = document.createElement('li');
  item.className = 'flex items-start gap-2';

  const text = document.createElement('span');
  text.className = 'text-subtle min-w-0 text-sm leading-relaxed';
  text.textContent = finding.detail;

  item.append(dot(finding.kind), text);
  return item;
}

/**
 * One module, and everything wrong under it.
 *
 * The module is written once, at the top, rather than at the start of each
 * sentence: a dozen lines each beginning with the same word is a log file, and
 * this card is read by somebody who wants to know which page to open.
 */
function block(group: HealthGroup): HTMLElement {
  const section = document.createElement('div');
  section.className = 'border-base-300 bg-base-200/40 rounded-box border p-3.5';

  const head = document.createElement('div');
  head.className = 'mb-2 flex items-center gap-2';

  const name = document.createElement('span');
  name.className = 'min-w-0 flex-1 truncate text-sm font-semibold';
  name.textContent = group.label;

  const count = document.createElement('span');
  count.className = 'badge badge-ghost badge-sm shrink-0 tabular-nums';
  count.textContent = String(group.findings.length);

  head.append(name, count);

  const list = document.createElement('ul');
  list.className = 'space-y-1.5';
  list.append(...group.findings.slice(0, SHOWN).map(line));

  section.append(head, list);

  /*
   * The rest, folded rather than dropped. A server with eleven stale channels
   * in one module should not read eleven lines to learn one thing — and it
   * must still be able to see all eleven.
   */
  if (group.findings.length > SHOWN) {
    const more = document.createElement('details');
    more.className = 'mt-1.5';

    const summary = document.createElement('summary');
    summary.className = 'text-faint cursor-pointer text-xs';
    summary.textContent = ct('healthMore', 'Show the rest').replace(
      '{count}',
      String(group.findings.length - SHOWN),
    );

    const rest = document.createElement('ul');
    rest.className = 'mt-1.5 space-y-1.5';
    rest.append(...group.findings.slice(SHOWN).map(line));

    more.append(summary, rest);
    section.append(more);
  }

  return section;
}

/** The card on the dashboard, shown only when it has something to say. */
export function initHealthCard(): void {
  const card = document.querySelector<HTMLElement>('[data-health]');
  const list = document.querySelector<HTMLElement>('[data-health-list]');
  const total = document.querySelector<HTMLElement>('[data-health-total]');
  if (!card || !list) return;

  void read().then((health) => {
    const groups = health?.groups ?? [];
    if (!health || groups.length === 0) return;

    if (total) total.textContent = String(health.total ?? health.findings.length);

    list.replaceChildren(...groups.map(block));
    card.classList.remove('hidden');
  });
}

/**
 * The same audit, straight after a save.
 *
 * "Saved" is the answer to a question nobody asked: what an administrator wants
 * to know is whether it now works. Pointing a module at a channel the bot
 * cannot write in saves perfectly and does nothing at all, and that gap is
 * where this product loses servers.
 *
 * Only the first finding is said, and only as a notice: the card above carries
 * the whole list, and a stack of red toasts over a successful save reads like a
 * failure.
 */
export async function warnAfterSave(): Promise<void> {
  const health = await read();
  const first = health?.findings[0];
  if (!first) return;

  toast(
    `${ct('healthAfterSave', 'Saved, but something is out of reach:')} ${first.message}`,
    'error',
  );
}
